import { getToken } from '@luminovo/auth';
import { uniqBy } from '@luminovo/commons';
import { http, OtsFullPart, PartLite } from '@luminovo/http-client';
import { convertOtsFullPartToPartLite, formatPart, formatPartLite } from '@luminovo/sourcing-core';
import { useQuery } from '@tanstack/react-query';
import { PartOption } from '../types';

/**
 * This hook is used to search for OTS parts in the Dialogs.
 * It returns a list of part options that can be used to display in the part search dialog.
 *
 * @param search - The search string to search for.
 * @param suggestedParts - The suggested parts to display in the part search dialog.
 *
 * TODO: Add support for searching IPNs
 */
export function usePartSearch({ search = '', suggestedParts = [] }: { search?: string; suggestedParts?: PartLite[] }): {
    partOptions: Array<PartOption>;
    isLoading: boolean;
} {
    const { data: otsPartSearchResults = [], isLoading: isLoadingSearch } = useQuery({
        queryKey: [{ search }],
        queryFn: async () => {
            const response = await http(
                'POST /parts/off-the-shelf/search/mpn/bulk',
                {
                    requestBody: {
                        items: [search],
                        rfq_context: 'OutsideRfQ',
                        search_external_apis: true,
                        use_elastic: true,
                    },
                },
                getToken(),
            );
            return response;
        },
        enabled: search?.length > 2,
        select: (res): OtsFullPart[] => Object.values(res.data).flatMap((x) => x),
    });

    const fromSearchResults: Array<PartOption> = otsPartSearchResults.map((otsPart: OtsFullPart) => {
        return {
            id: otsPart.id,
            part: convertOtsFullPartToPartLite(otsPart),
            label: formatPart(otsPart),
            description: otsPart.description ?? '',
            image: otsPart.image_url,
            isSuggested: suggestedParts.some((x) => x.id === otsPart.id),
        };
    });
    const fromSuggestedParts: Array<PartOption> = suggestedParts.map((part) => {
        return {
            id: part.id,
            part: part,
            label: formatPartLite(part),
            description: '',
            image: null,
            isSuggested: true,
        };
    });

    const allOtsParts: Array<PartOption> = [...fromSearchResults, ...fromSuggestedParts];

    return {
        partOptions: uniqBy(allOtsParts, (p) => p.id),
        isLoading: isLoadingSearch,
    };
}
